/**
 * Audio Element Editor
 * Uses MediaEdit for src field, custom controls for playback options
 */

import * as React from 'react';
import { cn, Label, Input, Switch } from '@ai-content-hub/content-kit';
import type { ElementEditorProps } from '@ai-content-hub/content-kit';
import { MediaEdit } from '../media';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { audioElementSchema, type AudioElementData } from './schema';

export interface AudioEditProps extends ElementEditorProps<AudioElementData> {}

export const AudioEdit: React.FC<AudioEditProps> = ({
    value,
    onChange,
    errors,
    disabled,
    className,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? audioElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    // Handle src change from MediaEdit
    const handleSrcChange = React.useCallback(
        (mediaData: { src: string; mediaType: string; title?: string }) => {
            onChange({
                ...value,
                src: mediaData.src,
                // Use media title if title is empty
                title: value.title || mediaData.title || '',
            });
        },
        [value, onChange]
    );

    // Handle field change
    const handleFieldChange = React.useCallback(
        <K extends keyof AudioElementData>(field: K, fieldValue: AudioElementData[K]) => {
            onChange({
                ...value,
                [field]: fieldValue,
            });
        },
        [value, onChange]
    );

    return (
        <div className={cn('audio-edit space-y-6', className)}>
            {/* Audio Source Section */}
            <div className="space-y-3">
                <Label className="text-sm font-medium">{t('audioSource')}</Label>
                <MediaEdit
                    value={{
                        src: value.src || '',
                        mediaType: 'audio',
                        title: value.title || '',
                    }}
                    onChange={handleSrcChange}
                    mediaType="audio"
                    layout="default"
                    disabled={disabled}
                    config={config}
                    language={language}
                />
                {errors?.src && (
                    <p className="text-sm text-destructive">{errors.src}</p>
                )}
            </div>

            {/* Title */}
            <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-medium">
                    {t('title', 'Title')}
                </Label>
                <Input
                    id="title"
                    value={value.title || ''}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder={t('audioTitlePlaceholder')}
                    disabled={disabled}
                />
            </div>

            {/* Playback Options */}
            <div className="space-y-4">
                <Label className="text-sm font-medium">{t('playbackOptions')}</Label>
                <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                        <Label htmlFor="autoplay" className="text-sm cursor-pointer">
                            {t('autoplay')}
                        </Label>
                        <Switch
                            id="autoplay"
                            checked={value.autoplay || false}
                            onCheckedChange={(checked) => handleFieldChange('autoplay', checked)}
                            disabled={disabled}
                        />
                    </div>

                    <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                        <Label htmlFor="loop" className="text-sm cursor-pointer">
                            {t('loop')}
                        </Label>
                        <Switch
                            id="loop"
                            checked={value.loop || false}
                            onCheckedChange={(checked) => handleFieldChange('loop', checked)}
                            disabled={disabled}
                        />
                    </div>

                    <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                        <Label htmlFor="showControls" className="text-sm cursor-pointer">
                            {t('showControls')}
                        </Label>
                        <Switch
                            id="showControls"
                            checked={value.showControls !== false}
                            onCheckedChange={(checked) => handleFieldChange('showControls', checked)}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AudioEdit;
